"use client";

import { useEffect, useMemo, useState } from "react";
import type { GoalAnalysis, GoalItem, GoalRoadmapStep } from "@/lib/api";

interface RoadmapResource {
  title: string;
  url: string;
  resource_type: string;
  description: string;
}

interface Props {
  goal: GoalItem;
  insight: GoalAnalysis;
  onClose: () => void;
  onApplyRoadmap: () => void;
  applyingRoadmap?: boolean;
  onStartStep?: (step: GoalRoadmapStep) => void;
  onCompleteStep?: (step: GoalRoadmapStep) => void;
  stepActionLoading?: string | null;
}

const RESOURCE_TYPE_ICON: Record<string, string> = {
  video: "🎬",
  article: "📝",
  podcast: "🎧",
  course: "🎓",
  tool: "🛠",
};

export default function RoadmapModal({
  goal,
  insight,
  onClose,
  onApplyRoadmap,
  applyingRoadmap = false,
  onStartStep,
  onCompleteStep,
  stepActionLoading,
}: Props) {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [resources, setResources] = useState<Record<number, RoadmapResource[]>>({});
  const [loadingStep, setLoadingStep] = useState<number | null>(null);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const milestoneTitles = useMemo(
    () => new Set((goal.milestones || []).map((item) => item.title.trim().toLowerCase())),
    [goal.milestones]
  );

  const pct = Math.round(goal.progress_pct * 100);
  const doneSteps = insight.roadmap.filter((step) => step.status === "done").length;
  const totalSteps = insight.roadmap.length;

  const handleStepClick = async (index: number, step: GoalRoadmapStep) => {
    if (expandedStep === index) {
      setExpandedStep(null);
      return;
    }

    setExpandedStep(index);

    if (!resources[index]) {
      setLoadingStep(index);
      try {
        const { getRoadmapResources } = await import("@/lib/api");
        const result = await getRoadmapResources(goal.id, step.title, step.reason);
        setResources((prev) => ({ ...prev, [index]: result.resources }));
      } catch {
        setResources((prev) => ({ ...prev, [index]: [] }));
      } finally {
        setLoadingStep(null);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center lg:left-60 pointer-events-none">
      <div className="absolute inset-0 bg-black/35 backdrop-blur-sm pointer-events-auto lg:top-20" onClick={onClose} />

      <div className="relative flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[32px] border border-border bg-bg shadow-xl sm:rounded-[32px] pointer-events-auto">
        <div className="sticky top-0 z-10 border-b border-border bg-bg/95 px-5 py-5 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">Roadmap</p>
              <h2 className="mt-1 text-2xl font-semibold text-text">{goal.title}</h2>
              <p className="mt-1 text-sm text-muted">Your step-by-step path, with concrete activation buttons and just enough support to keep momentum.</p>
            </div>
            <button
              onClick={onClose}
              className="rounded-2xl border border-border bg-card px-3 py-2 text-xs font-semibold text-text-light transition-colors hover:bg-secondary"
            >
              Close
            </button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">Overall progress</span>
                <span className="text-sm font-semibold text-text">{pct}%</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-forest transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-text-light">
              <span className="rounded-full bg-card px-3 py-1.5 shadow-sm">
                {doneSteps}/{totalSteps} steps done
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="mb-4 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#6F59E8]" />
            <p className="text-sm font-semibold text-text">{goal.title}</p>
          </div>

          <div className="relative ml-3 border-l border-[#D7D0FB] pl-6">
            {insight.roadmap.map((step, index) => {
              const isExpanded = expandedStep === index;
              const isLoading = loadingStep === index;
              const stepResources = resources[index];
              const isPersistedMilestone = milestoneTitles.has(step.title.trim().toLowerCase());
              const isComplete = step.status === "done";
              const isInProgress = step.status === "in_progress";

              return (
                <div key={`${step.title}-${index}`} className="relative pb-5">
                  <button
                    onClick={() => void handleStepClick(index, step)}
                    className={`absolute -left-[38px] top-5 flex h-5 w-5 items-center justify-center rounded-full border ${
                      isComplete
                        ? "border-accent bg-accent text-white"
                        : isInProgress
                          ? "border-blue bg-blue text-white"
                          : "border-border bg-bg text-muted"
                    }`}
                  >
                    <span className="text-[10px] font-semibold">{isComplete ? "✓" : ""}</span>
                  </button>

                  <div
                    className={`rounded-[26px] border p-5 transition-colors ${
                      isExpanded ? "border-[#6F59E8]/20 bg-white shadow-sm" : "border-border bg-card"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-secondary px-2.5 py-1 text-[10px] font-semibold text-text-light">
                            Step {index + 1}
                          </span>
                          <span
                            className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                              isComplete
                                ? "bg-accent/15 text-accent"
                                : isInProgress
                                  ? "bg-blue/15 text-blue"
                                  : "bg-sand-pale text-earth"
                            }`}
                          >
                            {isComplete ? "done" : isInProgress ? "in progress" : isPersistedMilestone ? "saved as milestone" : "next"}
                          </span>
                        </div>
                        <h3 className="mt-3 text-xl font-semibold text-text">{step.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-text-light">{step.reason}</p>
                      </div>
                      <button
                        onClick={() => void handleStepClick(index, step)}
                        className="rounded-full border border-border bg-bg px-3 py-1.5 text-[10px] font-semibold text-text-light transition-colors hover:bg-secondary"
                      >
                        {isExpanded ? "Hide resources" : "See resources"}
                      </button>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => onCompleteStep?.(step)}
                        disabled={isComplete || !onCompleteStep || stepActionLoading === `complete:${step.title}`}
                        className="rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold text-text-light transition-colors hover:bg-sage-pale disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {stepActionLoading === `complete:${step.title}`
                          ? "Completing..."
                          : isComplete
                            ? "Completed"
                            : "Mark complete"}
                      </button>
                      <button
                        onClick={() => onStartStep?.(step)}
                        disabled={!onStartStep || stepActionLoading === `start:${step.title}`}
                        className="rounded-full bg-forest-med px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-forest disabled:opacity-50"
                      >
                        {stepActionLoading === `start:${step.title}` ? "Starting..." : "Start step"}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 border-t border-border/80 pt-4">
                        {isLoading && (
                          <div className="space-y-2 animate-pulse">
                            <div className="h-3 w-1/3 rounded bg-muted/10" />
                            <div className="h-10 rounded-2xl bg-muted/5" />
                            <div className="h-10 rounded-2xl bg-muted/5" />
                          </div>
                        )}

                        {!isLoading && stepResources && stepResources.length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">Helpful resources</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {stepResources.map((resource, resourceIndex) => (
                                <a
                                  key={`${resource.title}-${resourceIndex}`}
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rounded-full border border-border bg-bg px-3 py-1.5 text-xs font-medium text-text-light transition-colors hover:border-forest/20 hover:text-text"
                                >
                                  {RESOURCE_TYPE_ICON[resource.resource_type] || "🔗"} {resource.title}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {!isLoading && stepResources && stepResources.length === 0 && (
                          <p className="text-xs text-muted">No resource suggestions for this step yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-border bg-bg/95 px-5 py-4 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs leading-relaxed text-muted">
              Save the roadmap as milestones if you want the steps to update goal progress automatically as you work through them.
            </p>
            <button
              onClick={onApplyRoadmap}
              disabled={applyingRoadmap}
              className="rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-forest-med disabled:opacity-50"
            >
              {applyingRoadmap ? "Saving roadmap..." : "Save roadmap as milestones"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
